import {
    Box,
    Button,
    LinearProgress,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Theme,
    Typography
} from "@mui/material";
import {
    useEffect,
    useState
} from "react";
import { linearProgressClasses } from '@mui/material/LinearProgress';
import { DownwardIcon, UpwardIcon } from "../../../../assets/SvgIcons";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    HoldingsProps,
    HoldingsTableData
} from "./types";
import { RootStateType } from "../../../../redux-store/reducers";
import { getAnalyticsHoldings } from "../../../../redux-store/actions/analytics";
import { GetAnalyticsHoldingsResponseBody } from "../../../../redux-store/types/analytics";
import { HoldingsLoading } from "../../loadingComponents";

function getCumulativeData(data: {
    name: string;
    allocation: number;
}[]) {
    const modifiedData = [...data].sort((a, b) => (b?.allocation ?? 0) - (a?.allocation ?? 0));

    let cumulativeValue = 0;

    const cumulativeData = modifiedData?.reduce((acc: {
        name: string;
        allocation: number;
        cumulativeAllocation?: number;
    }[], item: {
        name: string;
        allocation: number;
    }) => {
        cumulativeValue += item?.allocation ?? 0;

        acc.push({
            name: item?.name ?? "",
            allocation: item?.allocation ?? 0,
            cumulativeAllocation: cumulativeValue
        });
        return acc;
    }, []) ?? [];

    return cumulativeData
}

const innerTabs = ["Sectors", "Companies"]

const DataTable = (
    {
        data,
        tableFirstColumnName
    }: {
        data: {
            name: string;
            allocation: number;
            cumulativeAllocation?: number
        }[];
        tableFirstColumnName: string;
    }
): JSX.Element => {
    const [isShowMore, setShowMore] = useState<boolean>(false)

    const handleShowMore = () => {
        setShowMore(prevState => !prevState)
    }

    const modifiedData = isShowMore ? data : data.slice(0, 5)

    if ((data ?? []).length === 0) {
        return (
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <Typography
                    variant="body_600"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary
                    })}
                >
                    No Data Found
                </Typography>
            </Box>
        )
    }


    return (
        <>
            <TableContainer sx={{ maxHeight: "400px" }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell
                                sx={(theme: Theme) => ({
                                    ...theme.typography.subtitle_500,
                                    color: theme.palette.text.secondary,
                                    padding: 0,
                                })}
                            >
                                {tableFirstColumnName}
                            </TableCell>
                            <TableCell
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.secondary,
                                    ...theme.typography.subtitle_500,
                                })}
                            >
                                Allocation
                            </TableCell>
                            <TableCell
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.secondary,
                                    ...theme.typography.subtitle_500,
                                })}
                            >
                                Cumulative Allocation
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {modifiedData.map((obj, index) => (
                            <TableRow key={index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        fontFamily: theme.typography.fontFamily,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            color: theme.palette.primary[500]
                                        },
                                        textTransform: 'capitalize',
                                        padding: 0
                                    })}
                                >
                                    {obj?.name}
                                </TableCell>
                                <TableCell
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary,
                                        ...theme.typography.body_500,
                                        fontFamily: theme.typography.fontFamily,
                                    })}
                                >
                                    {`${!Number.isNaN(Number(obj?.allocation ?? 0)) ?
                                        Math.round((obj?.allocation ?? 0) * 100) / 100 : '0'}%`}
                                </TableCell>
                                <TableCell sx={{ width: '50%' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box sx={{ width: "100%", marginRight: "12px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={obj?.cumulativeAllocation ?? 0}
                                                sx={(theme: Theme) => ({
                                                    background: '#D9D9D9',
                                                    borderRadius: '15px',
                                                    [`&.${linearProgressClasses.colorPrimary}`]: {
                                                        background: 'D9D9D9'
                                                    },
                                                    [`& .${linearProgressClasses.bar}`]: {
                                                        background: theme.palette.primary[800]
                                                    }
                                                }
                                                )}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: 700,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.primary[500]
                                                })}
                                            >
                                                {`${!Number.isNaN(Number(obj?.cumulativeAllocation ?? 0)) ?
                                                    Math.round((obj?.cumulativeAllocation ?? 0) * 100) / 100 : '0'}%`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer >
            {((data ?? []).length > 5) && (
                <Button
                    variant="outlined"
                    sx={{
                        padding: '7px 0px',
                        border: 'none',
                        mt: '15px',
                        '&:hover': {
                            border: 'none',
                            background: 'transparent'
                        }
                    }}
                    onClick={handleShowMore}
                >
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500],
                            border: 'none',
                            marginRight: '3px',
                            cursor: 'pointer',
                            lineHeight: "20x",
                            letterSpacing: "0.01px"
                        })}
                    >
                        {isShowMore ? 'Show less' : 'Show all'}
                    </Typography>
                    {isShowMore ? <UpwardIcon /> : <DownwardIcon />}
                </Button>
            )}
        </>
    )
};

export default function Holdings({
    clientCode,
    portfolioId
}: HoldingsProps): JSX.Element {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<HoldingsTableData>({} as HoldingsTableData)
    const [selectedOuterTab, setSelectedOuterTab] = useState<number>(0)
    const [selectedInnerTab, setSelectedInnerTab] = useState("Sectors")

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async (clientCode: string, portfolioId: string) => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId
            }

            const response = await (dispatch(getAnalyticsHoldings(payload)) as unknown as Promise<GetAnalyticsHoldingsResponseBody>);

            const modifiedResponse = {
                ...response,
                result: response?.result?.map((resultObj) => ({
                    ...resultObj,
                    companies: getCumulativeData(resultObj?.companies?.map((companyObj) => ({
                        name: companyObj?.companyName ?? "",
                        allocation: companyObj?.allocation ?? 0,
                    })) ?? []),
                    sectors: getCumulativeData(resultObj?.sectors?.map((sectorObj) => ({
                        name: sectorObj?.sectorName ?? "",
                        allocation: sectorObj?.allocation ?? 0,
                    })) ?? []),
                })) ?? []
            }

            setTableData(modifiedResponse)
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(clientCode, portfolioId)
    }, [clientCode, portfolioId])

    const outerTabValues = tableData?.result?.map(obj => ({
        categoryName: obj?.categoryName ?? "",
        categoryPerentage: obj?.categoryPerentage ?? 0
    })) ?? []

    const equityData = tableData?.result?.find(obj => obj.categoryName === "Equity")
    const debtData = tableData?.result?.find(obj => obj.categoryName === "Debt")

    const handleOuterTabChange = (event: React.SyntheticEvent, activeTab: number) => {
        setSelectedInnerTab("Sectors")
        setSelectedOuterTab(activeTab);
    };

    const handleInnerTabChange = (activeTab: string) => {
        setSelectedInnerTab(activeTab);
    };

    if (isLoading) {
        return <HoldingsLoading />
    }

    if (!isLoading && !tableData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Holdings
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        Something went wrong. Please try again.
                    </Typography>
                </Box>
            </Box>

        )
    }

    return (
        <Box>
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineheight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Holdings
            </Typography>
            <Box sx={(theme: Theme) => ({
                border: `1px solid ${theme.palette.text.secondary}B2`,
                borderRadius: '25px',
                marginTop: "35px"
            })}
            >
                <Tabs
                    value={selectedOuterTab}
                    onChange={handleOuterTabChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTabs-indicator': {
                            display: 'none'
                        }
                    }}
                >
                    {outerTabValues.map((obj, index) => (
                        <Tab
                            key={index}
                            label={
                                <Typography
                                    variant='subtitle_300'
                                    sx={(theme: Theme) => ({
                                        color: selectedOuterTab === index ? theme.palette.textDark.primary : theme.palette.text.secondary,
                                        fontWeight: 500,
                                        textTransform: 'capitalize'
                                    })}
                                >
                                    {obj?.categoryName ?? ""}  {`${!Number.isNaN(Number(obj?.categoryPerentage ?? 0)) ?
                                        Math.round((obj?.categoryPerentage ?? 0) * 100) / 100
                                        : '0'}%`}
                                </Typography>
                            }
                            sx={(theme: Theme) => ({
                                backgroundColor: selectedOuterTab === index ? theme.palette.primary[500] : theme.palette.common.white,
                                borderRadius: "25px",
                            })}
                        />
                    ))}
                </Tabs>
            </Box>
            <Box sx={{ marginTop: '45px' }}>
                <Box sx={(theme: Theme) => ({ borderBottom: `0.5px solid ${theme.palette.text.secondary}B2` })}>
                    {innerTabs.map(tab => (
                        <Button
                            key={tab}
                            variant={selectedInnerTab === tab ? 'contained' : 'outlined'}
                            onClick={() => handleInnerTabChange(tab)}
                            sx={(theme: Theme) => ({
                                padding: '5px 15px',
                                borderRadius: '7px 7px 0 0',
                                marginRight: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                background: selectedInnerTab === tab ? theme.palette.primary[500] : theme.palette.grey[50],
                                '&:hover': {
                                    border: 'none',
                                    backgroundColor: selectedInnerTab === tab ? theme.palette.primary[800] : theme.palette.grey[100],
                                },
                            })}
                        >
                            <Typography variant='body_500' sx={(theme: Theme) => ({
                                color: selectedInnerTab === tab ? theme.palette.textDark.primary : theme.palette.text.secondary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            })}>
                                {tab}
                            </Typography>
                        </Button>
                    ))}
                </Box>
            </Box>
            <Box sx={{ marginTop: "45px" }}>
                {selectedInnerTab === "Sectors" && (
                    <DataTable
                        data={selectedOuterTab === 0 ? equityData?.sectors ?? [] : debtData?.sectors ?? []}
                        tableFirstColumnName="Sector"
                    />
                )}
                {selectedInnerTab === "Companies" && (
                    <DataTable
                        data={selectedOuterTab === 0 ? equityData?.companies ?? [] : debtData?.companies ?? []}
                        tableFirstColumnName="Company"
                    />
                )}
            </Box>
        </Box>
    )
}